/* eslint-disable max-lines */
import { handleActions, createAction } from "redux-actions";
import {
  api,
  generateUUID,
  checkEmptyObject,
  notification,
  isAllowedValue,
  getMultiLevelField,
  localStorage
} from "../../services";
import { ControlType, PageRoute, LocalStorageKey, AdStatus, HashTag, PromotionFromPages, START_PAGE_PARAM } from "../../models";
import { getAdFormConfig } from "../../dataConfig";
import history, { navigateTo404Page } from "../../history";
import {
  adFormInterface,
  rootReducerInterface
} from "../../typings/interfaces";
import {
  AdFormConfig,
  AdI,
  ControlI,
  ControlOptionI,
  currentValues,
  Group,
  UpdateDataI
} from "../../typings/routesTypes";
import { mapCategoryToHr, mapModuleToHr, mapModuleToEn } from "../LanguageMapper";
import ReactGA from "react-ga4";
import { ModuleName } from "../../models";

const AD_CONTROLS_GET = "AD_CONTROLS_GET";
const AD_UPDATE = "AD_UPDATE";
const AD_DATA_GET = "AD_DATA_GET";
const AD_DATA_SET = "AD_DATA_SET";
const AD_DATA_CLEAR = "AD_DATA_CLEAR";
const AD_CONTROLS_CLEAR = "AD_CONTROLS_CLEAR";
const AD_APPLY_MODEL_SUGGESTED_VALUE = "AD_GET_MODEL_SUGGESTED_VALUE";

const handleGetControlsValueError = async (
  showErrorOnFetch: any,
  allowNotFound: boolean,
  response: any
) => {
  switch (response.status) {
    case 401:
      api.handleUnauthorizedError();
      break;

    case 400:
      showErrorOnFetch();
      break;

    case 404:
      if (!allowNotFound) {
        notification.showError("Oglas nije dostupan");
        navigateTo404Page();
      }
      break;

    default:
      await api.handleCommonError(response);
      history.replace(`/`);
      break;
  }

  throw response;
};

const handleUpdateAdDataError = async (response: any) => {
  switch (response.status) {
    case 401:
      api.handleUnauthorizedError();
      break;
    case 400:
      await api.handleBusinessError(response);
      break;
    default:
      await api.handleCommonError(response);
      break;
  }

  throw response;
};

const handleGetSuggestedValuesError = async (response: any) => {
  switch (response.status) {
    case 401:
      api.handleUnauthorizedError();
      break;

    case 404:
      break;

    default:
      await api.handleCommonError(response);
      break;
  }

  throw response;
};

// Modules where location should NOT be auto-filled from user profile
const MODULES_WITHOUT_AUTO_LOCATION = [ModuleName.realEstate, ModuleName.tourism];

// eslint-disable-next-line complexity,max-lines-per-function
const parseControlValues = (control: any, ad: AdI, userData, module?: string) => {
  if (!ad) {
    return undefined;
  }

  const value: any = {};

  switch (control.type) {
    case ControlType.suggestedTitle:
    case ControlType.suggestedAITitle:
      if (isAllowedValue(ad[control.field])) {
        value[control.field] = ad[control.field];
      }
      break;

    case ControlType.suggestedAIDescription:
      if (isAllowedValue(ad[control.field])) {
        value[control.field] = ad[control.field];
      }
      break;
    case ControlType.singleColorSelector:
    case ControlType.dropdown:
    case ControlType.yearPicker:
    case ControlType.inputText:
    case ControlType.inputTextList:
    case ControlType.switch:
    case ControlType.monthYearPicker:
    case ControlType.datePicker:
    case ControlType.multiSelector:
    case ControlType.radioSelector:
      if (isAllowedValue(ad[control.field])) {
        value[control.field] = ad[control.field];
      }
      break;

    case ControlType.horsePower:
    case ControlType.money:
      if (isAllowedValue(ad[control.field])) {
        value[control.field] = ad[control.field];
        value[control.currencyField] = ad[control.currencyField];
      } else if (control.isRequired === false) {
        value[control.field] =
          ad[control.field] === 0 ? null : ad[control.field];
        value[control.currencyField] = ad[control.currencyField];
      }
      break;

    case ControlType.map:
      if (isAllowedValue(ad[control.latitudeField])) {
        value[control.latitudeField] = ad[control.latitudeField];
      }
      if (isAllowedValue(ad[control.longitudeField])) {
        value[control.longitudeField] = ad[control.longitudeField];
      }
      break;

    case ControlType.photoUploader: {
      const photos = ad[control.field];
      if (photos && photos.length) {
        value[control.field] = photos;
      }
      break;
    }

    case ControlType.multiLevel: {
      // Auto-fill location from user profile for all modules EXCEPT real-estate and tourism
      const shouldAutoFillLocation = userData &&
        control.dataSource === "location" &&
        !MODULES_WITHOUT_AUTO_LOCATION.includes(mapModuleToEn(module));

      if (shouldAutoFillLocation) {
        value.countryId = userData.countryId;
        value.countyId = userData.countyId;
        value.cityId = userData.cityId;
        value.settlementId = userData.settlementId;
        value.latitude = userData.latitude;
        value.longitude = userData.longitude;
        value.isPreciseLocation = false;
      } else {
        const fields = [];
        getMultiLevelField(control.child, fields);
        fields.forEach(field => {
          if (ad[field]) {
            value[field] = ad[field];
          }
        });
      }
      break;
    }

    case ControlType.multiFieldSelectorCb: {
      control.options.forEach(option => {
        if (isAllowedValue(ad[option.field])) {
          value[option.field] = ad[option.field];
        }
      });
      break;
    }

    case ControlType.multiFieldSelector: {
      control.options.forEach((option: ControlOptionI) => {
        if (isAllowedValue(ad[option.field])) {
          value[option.field] = ad[option.field];
        }
      });
      break;
    }

    case ControlType.tyres: {
      if (isAllowedValue(ad[control.field])) {
        value[control.field] = ad[control.field];
      }

      control.children.forEach(child => {
        if (isAllowedValue(ad[child.field])) {
          value[child.field] = ad[child.field];
        }
      });

      break;
    }

    case ControlType.phones:
      if (isAllowedValue(ad[control.field])) {
        value[control.field] = ad[control.field];
      }
      break;

    case ControlType.timeInput:
      if (isAllowedValue(ad[control.field])) {
        value[control.field] = ad[control.field];
      }
      break;

    case ControlType.locationMultiSelect: {
      const ids = ad[control.field];
      if (Array.isArray(ids) && ids.length > 0) {
        value[control.field] = ids;
      }
      break;
    }

    default:
      break;
  }

  return checkEmptyObject(value);
};

// Actions
export const getAdData = createAction(
  AD_DATA_GET,
  async (adId: string, showErrorOnFetch: Function, callback: Function, allowNotFound: boolean = false) => {

    try {
      const adData = await api.Ads.details(
        adId,
        handleGetControlsValueError.bind(this, showErrorOnFetch, allowNotFound)
      );

      callback && callback(true);

      return adData;
    } catch (error) {
      callback && callback(false);

      return null;
    }
  }
);

export const setAdData = createAction(AD_DATA_SET, (adData = null) => adData);

export const clearAdData = createAction(AD_DATA_CLEAR);

export const clearAdControls = createAction(AD_CONTROLS_CLEAR);

export const getControls = createAction(
  AD_CONTROLS_GET,
  // eslint-disable-next-line complexity
  async (
    module: string,
    category: string,
    completedSteps: number[],
    adData: any,
    userData: any,
  ) => {
    try {
      const adFormConfig: AdFormConfig = await getAdFormConfig(module);

      const adFormConfigCategory = adFormConfig.categories.find(
        c => c.nameHr === category
      );

      const configCategory =
        category && adFormConfigCategory
          ? adFormConfigCategory
          : adFormConfig.categories[0] || null;

      let steps = configCategory ? [...configCategory.steps] : [];

      steps = steps.map((step, index) => {
        // const groups = step.groups.map(parseGroupData.bind(this));

        const groups = step.controls.map(control => ({
          ...control,
          // scope of the multi level controls (makes, subcategories)
          module: mapModuleToEn(module),
          category: configCategory ? configCategory.name : undefined,
          id: generateUUID(),
          data: parseControlValues(control, adData, userData, module)
        }));

        // return {
        //   groups,
        //   name: step.name,
        //   description: step.description,
        //   isCompleted: completedSteps ? completedSteps.includes(index) : true,
        //   hasRequiredField: isHasAnyFieldRequired(groups)
        // };

        return {
          groups,
          name: step.name,
          description: step.description
          // isCompleted: completedSteps ? completedSteps.includes(index) : true,
          // hasRequiredField: isHasAnyFieldRequired(groups)
        };
      });

      return steps;
    } catch (error) {
      return [];
    }
  }
);

export const updateAd = createAction(
  AD_UPDATE,
  async (data: UpdateDataI, callback: Function) => {
    try {
      const { module, category } = data.formData;

      if (data.adId) {
        // Update ad data
        const { id, smartLink, code, status, isPromoted } = await api.Ads.update(
          module,
          data.adId,
          data.formData,
          handleUpdateAdDataError
        );

        if (
          !isPromoted
        ) {
          ReactGA.event({
            category: "Button click",
            action: "Click",
            label: 'Promocija oglasa - stranica ažuriranja oglasa'
          });
          history.replace(`${PageRoute.promote}/${id}?${START_PAGE_PARAM}=${PromotionFromPages.AdUpdated}${HashTag.adUpdated}`);
          notification.show("Oglas je uspješno ažuriran");

          callback && callback(true);
          localStorage.removeItem(LocalStorageKey.savedForm);
        } else {
          notification.show("Oglas je uspješno ažuriran");
          history.replace(
            `/${mapModuleToHr(module)}/${mapCategoryToHr(category, module)}${PageRoute.details
            }/${smartLink}/${data.adCode}${HashTag.adUpdated}`
          );

          callback && callback(true);
          localStorage.removeItem(LocalStorageKey.savedForm);
        }
      } else {
        // Create new ad
        const { id, smartLink, code, status, isPromoted } = await api.Ads.create(
          module,
          data.formData,
          handleUpdateAdDataError
        );

        if (
          !isPromoted
        ) {
          ReactGA.event({
            category: "Button click",
            action: "Click",
            label: 'Promocija oglasa - stranica objave oglasa'
          });

          history.replace(`${PageRoute.promote}/${id}?${START_PAGE_PARAM}=${PromotionFromPages.AdCreated}${HashTag.adNew}`);
          notification.show("Oglas je uspješno objavljen");

          callback && callback(true);
          localStorage.removeItem(LocalStorageKey.savedForm);
        } else {
          notification.show("Oglas je uspješno objavljen");
          // a newly created ad is promoted only when the free promotion from the deletion survey was applied
          notification.show("Vaš oglas je besplatno istaknut na 30 dana");

          callback && callback(true);
          localStorage.removeItem(LocalStorageKey.savedForm);

          history.replace(
            `/${mapModuleToHr(module)}/${mapCategoryToHr(category, module)}${PageRoute.details
            }/${smartLink}/${code}${HashTag.adNew}`
          );
        }
      }

    } catch (error) {
      callback && callback(false);
    }
  }
);



export const applyModelSuggestedValues = createAction(
  AD_APPLY_MODEL_SUGGESTED_VALUE,
  async (currentValues: currentValues, modelId: string) => {
    try {
      // get suggested values.
      const suggestedValues = await api.Model.getSuggestedValuesAsync(
        modelId,
        handleGetSuggestedValuesError
      );

      // add all suggested fields with suggested values if already filled: overwrite
      return { ...currentValues, ...suggestedValues };
    } catch (e) {
      return null;
    }
  }
);

// Selectors
export const selectControls = (state: rootReducerInterface) =>
  state.adForm.controls;
export const selectAdData = (state: rootReducerInterface) =>
  state.adForm.adData;

// Reducer
const initialState = {
  controls: null,
  adData: null
};

const clearFormData = (state: adFormInterface) => ({
  ...state,
  controls: null,
  adData: null
});

export default handleActions(
  {
    [AD_CONTROLS_GET]: (state: adFormInterface, { payload }) => ({
      ...state,
      controls: [...payload]
    }),
    [AD_DATA_GET]: (state: adFormInterface, { payload }) => ({
      ...state,
      adData: payload ? { ...payload } : null
    }),
    [AD_DATA_SET]: (state: adFormInterface, { payload }) => ({
      ...state,
      adData: { ...payload }
    }),
    [AD_DATA_CLEAR]: (state: adFormInterface) => ({
      ...state,
      adData: null
    }),
    [AD_CONTROLS_CLEAR]: (state: adFormInterface) => ({
      ...state,
      controls: null
    }),
    [AD_APPLY_MODEL_SUGGESTED_VALUE]: (state: adFormInterface, { payload }) => {
      if (payload) {
        return {
          ...state,
          adData: { ...payload }
        };
      }
      return state;
    },
    [AD_UPDATE]: clearFormData,
  },
  initialState
);



// WEBPACK FOOTER //
// ./src/routes/AdForm/adForm.ducks.ts